/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const CLOSE_POSITION_WITH_TOKEN_EXTENSIONS_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([1, 182, 135, 59, 155, 25, 99, 223]);

export function getClosePositionWithTokenExtensionsDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CLOSE_POSITION_WITH_TOKEN_EXTENSIONS_DISCRIMINATOR,
  );
}

export type ClosePositionWithTokenExtensionsInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountPositionAuthority extends string | AccountMeta = string,
  TAccountReceiver extends string | AccountMeta = string,
  TAccountPosition extends string | AccountMeta = string,
  TAccountPositionMint extends string | AccountMeta = string,
  TAccountPositionTokenAccount extends string | AccountMeta = string,
  TAccountToken2022Program extends
    | string
    | AccountMeta = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPositionAuthority extends string
        ? ReadonlySignerAccount<TAccountPositionAuthority> &
            AccountSignerMeta<TAccountPositionAuthority>
        : TAccountPositionAuthority,
      TAccountReceiver extends string
        ? WritableAccount<TAccountReceiver>
        : TAccountReceiver,
      TAccountPosition extends string
        ? WritableAccount<TAccountPosition>
        : TAccountPosition,
      TAccountPositionMint extends string
        ? WritableAccount<TAccountPositionMint>
        : TAccountPositionMint,
      TAccountPositionTokenAccount extends string
        ? WritableAccount<TAccountPositionTokenAccount>
        : TAccountPositionTokenAccount,
      TAccountToken2022Program extends string
        ? ReadonlyAccount<TAccountToken2022Program>
        : TAccountToken2022Program,
      ...TRemainingAccounts,
    ]
  >;

export interface ClosePositionWithTokenExtensionsInstructionData {
  discriminator: ReadonlyUint8Array;
}

export type ClosePositionWithTokenExtensionsInstructionDataArgs = {};

export function getClosePositionWithTokenExtensionsInstructionDataEncoder(): FixedSizeEncoder<ClosePositionWithTokenExtensionsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: CLOSE_POSITION_WITH_TOKEN_EXTENSIONS_DISCRIMINATOR,
    }),
  );
}

export function getClosePositionWithTokenExtensionsInstructionDataDecoder(): FixedSizeDecoder<ClosePositionWithTokenExtensionsInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getClosePositionWithTokenExtensionsInstructionDataCodec(): FixedSizeCodec<
  ClosePositionWithTokenExtensionsInstructionDataArgs,
  ClosePositionWithTokenExtensionsInstructionData
> {
  return combineCodec(
    getClosePositionWithTokenExtensionsInstructionDataEncoder(),
    getClosePositionWithTokenExtensionsInstructionDataDecoder(),
  );
}

export interface ClosePositionWithTokenExtensionsInput<
  TAccountPositionAuthority extends string = string,
  TAccountReceiver extends string = string,
  TAccountPosition extends string = string,
  TAccountPositionMint extends string = string,
  TAccountPositionTokenAccount extends string = string,
  TAccountToken2022Program extends string = string,
> {
  positionAuthority: TransactionSigner<TAccountPositionAuthority>;
  receiver: Address<TAccountReceiver>;
  position: Address<TAccountPosition>;
  positionMint: Address<TAccountPositionMint>;
  positionTokenAccount: Address<TAccountPositionTokenAccount>;
  token2022Program?: Address<TAccountToken2022Program>;
}

export function getClosePositionWithTokenExtensionsInstruction<
  TAccountPositionAuthority extends string,
  TAccountReceiver extends string,
  TAccountPosition extends string,
  TAccountPositionMint extends string,
  TAccountPositionTokenAccount extends string,
  TAccountToken2022Program extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: ClosePositionWithTokenExtensionsInput<
    TAccountPositionAuthority,
    TAccountReceiver,
    TAccountPosition,
    TAccountPositionMint,
    TAccountPositionTokenAccount,
    TAccountToken2022Program
  >,
  config?: { programAddress?: TProgramAddress },
): ClosePositionWithTokenExtensionsInstruction<
  TProgramAddress,
  TAccountPositionAuthority,
  TAccountReceiver,
  TAccountPosition,
  TAccountPositionMint,
  TAccountPositionTokenAccount,
  TAccountToken2022Program
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    positionAuthority: {
      value: input.positionAuthority ?? null,
      isWritable: false,
    },
    receiver: { value: input.receiver ?? null, isWritable: true },
    position: { value: input.position ?? null, isWritable: true },
    positionMint: { value: input.positionMint ?? null, isWritable: true },
    positionTokenAccount: {
      value: input.positionTokenAccount ?? null,
      isWritable: true,
    },
    token2022Program: {
      value: input.token2022Program ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.token2022Program.value) {
    accounts.token2022Program.value =
      "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb" as Address<"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("positionAuthority", accounts.positionAuthority),
      getAccountMeta("receiver", accounts.receiver),
      getAccountMeta("position", accounts.position),
      getAccountMeta("positionMint", accounts.positionMint),
      getAccountMeta("positionTokenAccount", accounts.positionTokenAccount),
      getAccountMeta("token2022Program", accounts.token2022Program),
    ],
    data: getClosePositionWithTokenExtensionsInstructionDataEncoder().encode(
      {},
    ),
    programAddress,
  } as ClosePositionWithTokenExtensionsInstruction<
    TProgramAddress,
    TAccountPositionAuthority,
    TAccountReceiver,
    TAccountPosition,
    TAccountPositionMint,
    TAccountPositionTokenAccount,
    TAccountToken2022Program
  >);
}

export interface ParsedClosePositionWithTokenExtensionsInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    positionAuthority: TAccountMetas[0];
    receiver: TAccountMetas[1];
    position: TAccountMetas[2];
    positionMint: TAccountMetas[3];
    positionTokenAccount: TAccountMetas[4];
    token2022Program: TAccountMetas[5];
  };
  data: ClosePositionWithTokenExtensionsInstructionData;
}

export function parseClosePositionWithTokenExtensionsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedClosePositionWithTokenExtensionsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 6,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      positionAuthority: getNextAccount(),
      receiver: getNextAccount(),
      position: getNextAccount(),
      positionMint: getNextAccount(),
      positionTokenAccount: getNextAccount(),
      token2022Program: getNextAccount(),
    },
    data: getClosePositionWithTokenExtensionsInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
